import * as React from 'react';
import { Fragment } from 'react';
import * as dust from '@density/dust/dist/tokens/dust.tokens';

import Editor, { EditorTreatments } from 'components/editor/editor';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { PlanDetail } from 'lib/api';
import { updatePlanDetailInRedux } from 'redux/features/planning/update-plan-detail-in-redux-thunk';
import { asyncFetchSpacesThunk } from 'redux/features/spaces/async-fetch-spaces-thunk';
import { SPLITS } from 'lib/treatments';
import { useTreatments, useTreatment } from 'contexts/treatments';
import FillCenter from 'components/fill-center/fill-center';
import AppBarFloor from 'components/app-bar-floor/app-bar-floor';
import { DisabledFloorView } from 'deprecation/disabled-floor-view';

const FloorPlanning: React.FC<{
  planDetail: PlanDetail;
  planImage: HTMLImageElement | null;
}> = ({ planDetail, planImage }) => {
  const dispatch = useAppDispatch();

  const densityAPIClient = useAppSelector(
    (state) => state.auth.densityAPIClient
  );

  const tokenCheckResponse = useAppSelector(
    (state) => state.auth.tokenCheckResponse
  );

  const handlePlanSaved = React.useCallback(
    (planDetail: PlanDetail) => {
      // update plan detail in redux
      dispatch(updatePlanDetailInRedux(planDetail));

      // refetch spaces query since some spaces may have been deleted or created
      dispatch(asyncFetchSpacesThunk());
    },
    [dispatch]
  );

  const treatments = useTreatments([
    SPLITS.IMPORT_DXF,
    SPLITS.EXPORT_DXF,
    SPLITS.PLANNING_FLOORPLAN_COMPONENT,
    SPLITS.HEIGHT_MAP,
    SPLITS.AUTOLAYOUT,
    SPLITS.SENSOR_COVERAGE,
  ]) as EditorTreatments;

  // this is the switch to enable this view entirely
  const isPlanningViewEnabled = useTreatment(SPLITS.VIEW_PLANNING);
  // this is the switch to show the link to new planner
  const isOAPlanningDisabled = useTreatment(SPLITS.OA_PLANNING_DISABLED);

  if (!isPlanningViewEnabled) return <DisabledFloorView />;

  if (isOAPlanningDisabled) {
    const plannerBaseUrl = window.location.origin.replace('oa.', 'planner.');
    const organizationId = tokenCheckResponse?.organization?.id || null;
    return (
      <Fragment>
        <AppBarFloor />
        <div style={{ backgroundColor: dust.Gray000, height: '100%' }}>
          <FillCenter>
            <div
              style={{
                padding: dust.Space4,
                border: `1px solid ${dust.Gray200}`,
                borderRadius: dust.Radius200,
                backgroundColor: dust.White,
              }}
            >
              Planner has moved!{' '}
              <a
                href={`${plannerBaseUrl}/${organizationId}/floorplans/${planDetail.id}`}
              >
                Click here
              </a>{' '}
              to edit this plan.
            </div>
          </FillCenter>
        </div>
      </Fragment>
    );
  }

  if (!densityAPIClient) {
    return null;
  }

  return (
    <Editor
      client={densityAPIClient}
      plan={planDetail}
      planImage={planImage}
      tokenCheckResponse={tokenCheckResponse || undefined}
      onPlanSaved={handlePlanSaved}
      treatments={treatments}
    />
  );
};

export default FloorPlanning;
